<template>
  <div class="chart-container__chart">
    <div class="chart-container__wrapper">
      <div class="chart-container__info">
        <transition name="fade">
          <span :key="fullPrice(price)" class="price text-bold">{{ fullPrice(price) }}</span>
        </transition>
        <transition name="fade">
            <span
                class="change"
                :key="changePercent"
                :class="[isPositiveChange ? 'text-green' : 'text-red']"
            >
              {{ formattedChange }}
            </span>
        </transition>
      </div>
      <div class="chart-container__buttons" v-once>
        <button
            v-if="buyLink"
            @click="$emit('buy')"
            class="btn btn-primary btn-lg"
        >
          {{ $t('prices.buy', { crypto: cryptoCurrency.name }) }}
        </button>

        <button
            v-if="sellLink"
            @click="$emit('sell')"
            class="btn btn-secondary btn-lg ml-16-bid"
        >
          {{ $t('prices.sell', { crypto: cryptoCurrency.name }) }}
        </button>
      </div>
    </div>
    <div class="price-chart" :class="{ 'is-loading': isLoading }">
      <PriceTrend
          v-if="isMounted && isReadyForRender"
          :fiatCurrency="selectedFiatCurrency"
          :chartData="series"
          :datePeriod="selectedTimeInterval.period"
          @update="updated"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import PriceTrend from '@/components/CryptoCurrency/PriceTrend.vue'
import getFullPrice from '@/helpers/fullPrice';

export default {
  name: 'MainChart',

  components: {
    PriceTrend,
  },

  props: {
    price: { type: [String, Number], required: false, default: null },
    change: { type: [String, Number], required: false, default: null },
    chartData: { type: Array, required: false, default: () => [] },
    buyLink: { type: String, required: false, default: null },
    sellLink: { type: String, required: false, default: null },
    cryptoCurrency: { type: Object, required: false },
    loading: { type: Boolean, required: false, default: true },
  },

  data () {
    return {
      isLoading: true,
      isMounted: false,
    };
  },

  mounted () {
    this.isMounted = true;
  },

  watch: {
    loading (isLoading) {
      if (!isLoading) {
        return;
      }
      this.isLoading = isLoading;
    },
  },

  computed: {
    ...mapState(['language']),
    ...mapState('cryptoCurrency', ['selectedFiatCurrency', 'selectedTimeInterval']),
    isReadyForRender () {
      return this.chartData.length > 0 && this.selectedTimeInterval.value;
    },
    series () {
      if (this.chartData.length > 0) {
        return this.chartData.map((item) => {
          return {
            date: (new Date(item[0] * 1000)).toISOString(),
            price: item[1],
          }
        });
      }

      return [];
    },
    changePercent () {
      if (this.chartData.length === 0) {
        return this.change;
      }
      // Find first historic item (oldest), where price is greater than 0
      const firstHistoricPrice = this.chartData.find((price) => price[1] > 0);
      if (!firstHistoricPrice) {
        return this.change;
      }
      const historicPrice = firstHistoricPrice[1];

      return (this.price - historicPrice) / historicPrice * 100;
    },
    isPositiveChange () {
      return this.changePercent > 0;
    },
    formattedChange () {
      const change = Number(this.changePercent).toFixed(3) + '%';

      return (this.changePercent > 0) ? '+' + change : change;
    },
  },

  methods: {
    updated () {
      this.isLoading = false;
    },

    fullPrice (value) {
      return getFullPrice(this.language, this.selectedFiatCurrency.code, value);
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'scss-vars';
@use "@assets/scss/components/prices/loader";

.price-chart {
  position: relative;
  min-height: 400px;
  height: 400px;

  @include loader.loader;

  &.is-loading:after {
    top: 25%;
  }

  & .is-hidden {
    opacity: 0;
    position: absolute;
    top: 0;
  }
}

.chart-container__chart {
  width: 100%;
  border: 1px solid #000000;
  border-radius: 6px;
  padding: 15px;

  .chart {
    width: 100%;
  }
}

@media screen and (max-width: scss-vars.$screen-md-max) {
  .chart-container__chart {
    padding: 5px;
  }
}

.chart-container {
  &__wrapper {
    display: flex;
    justify-content: space-between;
  }

  &__info {
    display: flex;
    flex-direction: column;

    .price {
      font-size: 32px;
    }

    .change {
      margin-top: 4px;
      font-size: 14px;
    }
  }

  &__buttons {
    display: flex;
  }

  @media screen and (max-width: scss-vars.$screen-sm-max) {
    &__wrapper {
      flex-direction: column;
    }

    &__info {
      .price {
        text-align: center;
      }

      .change {
        text-align: center;
      }
    }

    &__buttons {
      justify-content: center;
      margin-top: 28px;
    }
  }
}
// @TODO code was moved to global css. Check how it will be fork without local styles.
.fade-enter-active {
  transition: opacity 1s;
}
.fade-enter,
.fade-enter-from {
  opacity: 0.2;
}
.fade-leave-active {
  display: none;
}
</style>
